import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function GeneralErrorPage({ error, resetError }) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  const handleTryAgain = () => {
    if (resetError) {
      resetError();
    }
    navigate('/dashboard');
  };

  const handleGoHome = () => {
    if (resetError) {
      resetError();
    }
    navigate('/');
  };

  // Get component stack and other details
  const getErrorDetails = () => {
    const details = {
      message: error?.message || error?.toString() || 'Unknown error',
      stack: error?.stack || 'No stack trace available',
      componentStack: error?.componentStack || 'No component stack available',
      location: window.location.pathname,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
    return details;
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="min-h-screen w-full relative flex items-center justify-center overflow-hidden bg-gray-900">
      {/* Background Image with 30% opacity */}
      <div 
        className="absolute inset-0 bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/Error-Screen-Background.png)',
          backgroundSize: 'contain',
          opacity: 0.3
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl w-full">
        {/* Main Error Message */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-yellow-400 mb-6 leading-tight text-center">
          OHHHH, FUG MY TIGHT LITTLE P.....rogram has an error!<br />
          Try again, hung boy.
        </h1>
        
        {/* Error Summary */}
        {error && (
          <div className="bg-gray-800/80 backdrop-blur-sm rounded-lg p-6 mb-4 border border-yellow-400/30">
            <p className="text-yellow-400 font-semibold mb-2">Error Message:</p>
            <p className="text-gray-300 text-sm font-mono break-words">
              {errorDetails.message}
            </p>
            <p className="text-gray-400 text-xs mt-2">
              Location: <span className="text-blue-400">{errorDetails.location}</span>
            </p>
            <p className="text-gray-400 text-xs">
              Time: <span className="text-gray-300">{new Date(errorDetails.timestamp).toLocaleString()}</span>
            </p>
          </div>
        )}
        
        {/* Detailed Debug Information (Collapsible) */}
        <div className="mb-8">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-yellow-400 hover:text-yellow-300 text-sm underline mb-4"
          >
            {showDetails ? '▼ Hide' : '▶ Show'} Detailed Debugging Information
          </button>
          
          {showDetails && (
            <div className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-6 border border-red-500/30 text-left max-h-96 overflow-y-auto">
              <div className="mb-4">
                <p className="text-red-400 font-semibold mb-2">Stack Trace:</p>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded">
                  {errorDetails.stack}
                </pre>
              </div>
              
              <div className="mb-4">
                <p className="text-red-400 font-semibold mb-2">Component Stack:</p>
                <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono bg-black/30 p-3 rounded">
                  {errorDetails.componentStack}
                </pre>
              </div>
              
              <div className="mb-4">
                <p className="text-red-400 font-semibold mb-2">Browser Information:</p>
                <p className="text-xs text-gray-300 font-mono bg-black/30 p-3 rounded break-all">
                  {errorDetails.userAgent}
                </p>
              </div>
              
              <div className="mb-4">
                <p className="text-red-400 font-semibold mb-2">Current State:</p>
                <pre className="text-xs text-gray-300 font-mono bg-black/30 p-3 rounded">
                  {JSON.stringify({
                    pathname: errorDetails.location,
                    timestamp: errorDetails.timestamp,
                    errorType: error?.name || 'Error',
                  }, null, 2)}
                </pre>
              </div>
              
              <div className="bg-blue-900/30 border border-blue-500/30 rounded p-3 mt-4">
                <p className="text-blue-300 text-xs font-semibold mb-2">💡 For Developers:</p>
                <p className="text-blue-200 text-xs">
                  Copy the above information and share with the development team for faster diagnosis and resolution.
                  The stack trace will help identify the exact line of code causing the issue.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={handleTryAgain}
            className="px-8 py-3 bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-bold rounded-lg transition-colors duration-200 min-w-[200px]"
          >
            Try Again
          </button>
          <button
            onClick={handleGoHome}
            className="px-8 py-3 bg-transparent border-2 border-yellow-400 hover:bg-yellow-400/10 text-yellow-400 font-bold rounded-lg transition-colors duration-200 min-w-[200px]"
          >
            Go to Home
          </button>
        </div>
        
        {/* Additional Help Text */}
        <p className="text-gray-400 text-sm mt-8">
          If this problem persists, please contact your system administrator or try refreshing the page.
        </p>
      </div>
    </div>
  );
}

export default GeneralErrorPage;
